import { CalendarCheck2, DollarSign, Sparkles, Users } from "lucide-react";
import { StatCard } from "@/components/schedora/StatCard";
import { useStaffSnapshot } from "@/context/AppContext";
import { StatusBadge } from "@/components/schedora/StatusBadge";

const today = new Date().toISOString().slice(0, 10);

export default function StaffDashboard() {
  const snapshot = useStaffSnapshot(today);

  if (!snapshot) {
    return (
      <div className="rounded-xl border border-dashed bg-card p-6 text-sm text-muted-foreground">
        Your staff profile is still syncing. Please refresh in a moment.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-foreground">My Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Your appointments, services completed, and commission totals in one place.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard title="Upcoming" value={snapshot.stats.upcomingCount} icon={CalendarCheck2} trend="Confirmed and pending" trendUp />
        <StatCard title="Completed" value={snapshot.stats.completedCount} icon={Sparkles} trend="Services finished" trendUp />
        <StatCard title="Revenue Handled" value={`$${snapshot.stats.revenueHandled.toFixed(0)}`} icon={Users} trend="Completed services total" trendUp />
        <StatCard title="Commission" value={`$${snapshot.stats.commissionTotal.toFixed(2)}`} icon={DollarSign} trend={`${snapshot.staffMember.commissionRate}% rate`} trendUp />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-3 text-lg font-heading font-semibold">Today’s Schedule</h2>
          <div className="space-y-3">
            {snapshot.todayAppointments.map((appointment) => (
              <div key={appointment.id} className="rounded-xl border bg-card p-4 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-foreground">{appointment.serviceName}</h3>
                    <p className="text-sm text-muted-foreground">{appointment.clientName}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {appointment.date} · {appointment.timeLabel}
                    </p>
                  </div>
                  <StatusBadge status={appointment.status} />
                </div>
              </div>
            ))}
            {snapshot.todayAppointments.length === 0 && (
              <div className="rounded-xl border border-dashed bg-card p-5 text-sm text-muted-foreground">
                No appointments scheduled for you today.
              </div>
            )}
          </div>
        </section>

        <section>
          <h2 className="mb-3 text-lg font-heading font-semibold">Completed Services</h2>
          <div className="space-y-3">
            {snapshot.completed.slice().reverse().slice(0, 8).map((appointment) => (
              <div key={appointment.id} className="rounded-xl border bg-card p-4 shadow-soft">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-foreground">{appointment.serviceName}</h3>
                    <p className="text-sm text-muted-foreground">{appointment.clientName}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{appointment.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-foreground">
                      ${((appointment.servicePrice * snapshot.staffMember.commissionRate) / 100).toFixed(2)}
                    </p>
                    <p className="text-xs text-muted-foreground">Commission</p>
                  </div>
                </div>
              </div>
            ))}
            {snapshot.completed.length === 0 && (
              <div className="rounded-xl border border-dashed bg-card p-5 text-sm text-muted-foreground">
                Completed services will appear here once you start finishing appointments.
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
